import { useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  useConsolidateDocuments,
  useDeleteDocument,
  useUpdateDocument,
} from "@/hooks/documents";
import { REVIEWED_AT_METADATA_KEY, SIGNAL_LABELS } from "@/lib/knowledge/documentHealth";
import { cn } from "@/lib/utils";
import type {
  DocumentHealth,
  DocumentHealthSignal,
  DocumentSignalResult,
} from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface ReviewQueueDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  queue: DocumentHealth[];
  /** Every document, so merge can offer sensible partners. */
  allDocuments: DocumentWithMetadata[];
  /** Hands the document to the existing details drawer for editing. */
  onUpdateDocument: (document: DocumentWithMetadata) => void;
}

const SIGNAL_ORDER: DocumentHealthSignal[] = ["stale", "unused", "conflicting"];

const SignalPill = ({ result }: { result: DocumentSignalResult }) => {
  const styles =
    result.state === "flagged"
      ? "border-genie-secondary/40 bg-genie-secondary/10 text-genie-secondary"
      : result.state === "unavailable"
        ? "border-gray-200 bg-gray-50 text-gray-400"
        : "border-genie-primary/30 bg-genie-primary/10 text-genie-primary";

  return (
    <div className={cn("rounded-xl border px-3 py-2.5", styles)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide">
          {SIGNAL_LABELS[result.signal]}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
          {result.state === "flagged"
            ? "Needs a look"
            : result.state === "unavailable"
              ? "Not available yet"
              : "Looks fine"}
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-gray-600">{result.reason}</p>
    </div>
  );
};

/**
 * One document at a time, with the four decisions Sam asked for:
 * keep, update, merge, delete. Nothing here is a bulk table.
 */
const ReviewQueueDrawer = ({
  isOpen,
  onClose,
  queue,
  allDocuments,
  onUpdateDocument,
}: ReviewQueueDrawerProps) => {
  const [index, setIndex] = useState(0);
  const [decided, setDecided] = useState<Set<string>>(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeIds, setMergeIds] = useState<string[]>([]);
  const [mergeName, setMergeName] = useState("");
  const [deleteOriginals, setDeleteOriginals] = useState(false);

  const { mutateAsync: updateDocument, isPending: isKeeping } = useUpdateDocument();
  const { mutate: deleteDocument, isPending: isDeleting } = useDeleteDocument();
  const { mutateAsync: consolidate, isPending: isMerging } = useConsolidateDocuments();

  const pending = useMemo(
    () => queue.filter((item) => !decided.has(item.documentId)),
    [queue, decided],
  );
  const current = pending[Math.min(index, Math.max(0, pending.length - 1))] ?? null;

  useEffect(() => {
    if (!isOpen) {
      setIndex(0);
      setDecided(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    setMergeOpen(false);
    setMergeIds([]);
    setMergeName(current?.document.name ?? "");
    setDeleteOriginals(false);
  }, [current?.documentId, current?.document.name]);

  const mergeCandidates = useMemo(() => {
    if (!current) return [];
    const folderId = current.document.folder_id;
    const others = allDocuments.filter((doc) => doc.id !== current.documentId);
    const sameFolder = folderId ? others.filter((doc) => doc.folder_id === folderId) : [];
    return (sameFolder.length > 0 ? sameFolder : others).slice(0, 40);
  }, [current, allDocuments]);

  const advance = (documentId: string) => {
    setDecided((prev) => new Set(prev).add(documentId));
    setIndex(0);
  };

  const handleKeep = async () => {
    if (!current) return;
    try {
      await updateDocument({
        document: current.document,
        updates: {
          metadata: { [REVIEWED_AT_METADATA_KEY]: new Date().toISOString() },
        },
      });
      toast.success("Kept as is");
      advance(current.documentId);
    } catch {
      // useUpdateDocument already surfaces the error toast.
    }
  };

  const handleUpdate = () => {
    if (!current) return;
    const document = current.document;
    advance(current.documentId);
    onClose();
    onUpdateDocument(document);
  };

  const handleDelete = () => {
    if (!current) return;
    const documentId = current.documentId;
    deleteDocument(documentId, { onSuccess: () => advance(documentId) });
  };

  const handleMerge = async () => {
    if (!current || mergeIds.length === 0) return;
    try {
      await consolidate({
        documentIds: [current.documentId, ...mergeIds],
        docName: mergeName.trim() || undefined,
        prune: deleteOriginals,
      });
      advance(current.documentId);
    } catch {
      // useConsolidateDocuments already surfaces the error toast.
    }
  };

  const busy = isKeeping || isDeleting || isMerging;
  const position = queue.length - pending.length + 1;

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Review queue"
      description="Decide on one document at a time: keep it, update it, merge it, or remove it."
      size="lg"
      contentClassName="bg-[#F9FAFC]"
    >
      {!current ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-genie-primary/10">
            <Check className="h-6 w-6 text-genie-primary" />
          </div>
          <p className="text-base font-semibold text-gray-900">Queue cleared</p>
          <p className="max-w-sm text-sm text-gray-600">
            Every flagged document has a decision. New ones appear here as documents age or drift
            out of use.
          </p>
          <Button variant="outline" onClick={onClose} className="mt-2">
            Close
          </Button>
        </div>
      ) : (
        <div className="space-y-5 pb-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Document {Math.min(position, queue.length)} of {queue.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Previous document"
                disabled={index === 0}
                onClick={() => setIndex((value) => Math.max(0, value - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Skip to next document"
                disabled={index >= pending.length - 1}
                onClick={() => setIndex((value) => Math.min(pending.length - 1, value + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-semibold text-gray-900">{current.document.name}</h3>
            <p className="mt-1 text-xs text-gray-500">
              {current.document.folder?.name
                ? `In ${current.document.folder.name}`
                : "Not in a folder"}
              {current.document.dependent_agents?.length
                ? ` · Linked to ${current.document.dependent_agents.length} ${
                    current.document.dependent_agents.length === 1 ? "Genie" : "Genies"
                  }`
                : " · Not linked to a Genie"}
            </p>

            <div className="mt-4 space-y-2">
              {SIGNAL_ORDER.map((signal) => (
                <SignalPill key={signal} result={current.signals[signal]} />
              ))}
            </div>
          </div>

          {mergeOpen && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h4 className="text-sm font-semibold text-gray-900">Merge with</h4>
              <p className="mt-1 text-xs text-gray-500">
                Pick the documents to fold into one. The merged document keeps everything; the
                originals stay unless you say otherwise.
              </p>

              <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                {mergeCandidates.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500">
                    No other documents available to merge with.
                  </p>
                ) : (
                  mergeCandidates.map((doc) => {
                    const checked = mergeIds.includes(doc.id);
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() =>
                          setMergeIds((prev) =>
                            checked ? prev.filter((id) => id !== doc.id) : [...prev, doc.id],
                          )
                        }
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all",
                          checked
                            ? "border-genie-primary bg-genie-primary/5"
                            : "border-gray-200 bg-white hover:border-gray-300",
                        )}
                      >
                        <Checkbox checked={checked} className="pointer-events-none shrink-0" />
                        <span className="truncate text-sm text-gray-800">{doc.name}</span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Name the merged document
                  </label>
                  <Input
                    value={mergeName}
                    onChange={(event) => setMergeName(event.target.value)}
                    placeholder="e.g. Membership policies"
                    className="h-11"
                  />
                </div>
                <label className="flex items-start gap-2.5 text-sm text-gray-700">
                  <Checkbox
                    checked={deleteOriginals}
                    onCheckedChange={(value) => setDeleteOriginals(value === true)}
                    className="mt-0.5"
                  />
                  <span>
                    Remove the original documents afterwards
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Off by default — keep the originals until you have read the merge.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleKeep} disabled={busy}>
              {isKeeping && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Keep
            </Button>
            <Button variant="outline" onClick={handleUpdate} disabled={busy}>
              Update
            </Button>
            {mergeOpen ? (
              <>
                <Button
                  onClick={handleMerge}
                  disabled={busy || mergeIds.length === 0}
                  className="bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-lg shadow-genie-primary/30 hover:from-genie-primary-hover hover:to-genie-secondary disabled:opacity-50"
                >
                  {isMerging && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Merge {mergeIds.length > 0 ? `${mergeIds.length + 1} documents` : ""}
                </Button>
                <Button variant="ghost" onClick={() => setMergeOpen(false)} disabled={busy}>
                  Cancel merge
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setMergeOpen(true)} disabled={busy}>
                Merge
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={busy}
              className="ml-auto border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </div>
        </div>
      )}
    </BaseSheet>
  );
};

export default ReviewQueueDrawer;
